import { formatRecordingDate as formatDate, formatRecordingDuration as formatDuration } from "../recordingLabel";
export { formatRecordingDuration as formatDuration } from "../recordingLabel";
import { useEffect, useState, useSyncExternalStore, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getDemoRecording, listOperationalRecordings, operationalKeys, authDataIdentity, subscribeAuthDataIdentity } from "@rekonas/data-access";
import { SummaryEmailAction, RecentSummaryEmails } from "../components/SummaryEmails";
import type { OperationalRecordingSummary } from "@rekonas/domain";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { UploadPanel } from "../components/upload/UploadPanel";
import { useOperationalWorkspace } from "../operationalWorkspace";

export function shouldExposeWebUploads(flag: string | undefined, permissions: readonly string[]): boolean {
  return flag === "1" && permissions.includes("recordings.upload");
}

export function WebUploadGate(props: { flag: string | undefined; permissions: readonly string[]; children: ReactNode }) {
  return shouldExposeWebUploads(props.flag, props.permissions) ? props.children : null;
}

export function operationalRecordingPath(workspaceRef: string, recordingRef: string): string {
  return `/recordings/${encodeURIComponent(workspaceRef)}/${encodeURIComponent(recordingRef)}`;
}


export function productStatus(recording: OperationalRecordingSummary): { label: string; tone: string; viewer: boolean } {
  if (recording.signals === "failed") return { label: "Signals unavailable", tone: "attention", viewer: false };
  if (recording.signals !== "available") return { label: "Processing signals", tone: "processing", viewer: false };
  if (recording.viewer === "failed") return { label: "Viewer preparation failed", tone: "attention", viewer: false };
  if (recording.hypnogram === "failed") return { label: "Signals ready · sleep failed", tone: "attention", viewer: true };
  if (recording.hypnogram === "processing" || recording.hypnogram === "queued") {
    return { label: "Signals ready · sleep processing", tone: "processing", viewer: true };
  }
  if (recording.viewer !== "available") return { label: "Preparing viewer", tone: "processing", viewer: false };
  if (recording.hypnogram === "unsupported_mapping") return { label: "Signals ready · sleep unavailable", tone: "ready", viewer: true };
  return { label: "Ready", tone: "ready", viewer: true };
}

export function RecordingRow({ recording, workspaceRef, selection }: { recording: OperationalRecordingSummary; workspaceRef: string;
  selection?: {checked: boolean; disabled: boolean; toggle: () => void} }) {
  const status = productStatus(recording);
  return (
    <article className="operational-recording-row">
      <div className="recording-date">
        {selection && <label className="summary-select"><input type="checkbox" checked={selection.checked} disabled={selection.disabled}
          onChange={selection.toggle} aria-label={`Select recording ${formatDate(recording.display_date)} ${recording.recording_ref}`} /> Select</label>}
        <span>Night recording</span>
        <strong>{formatDate(recording.display_date)}</strong>
        <small>{recording.recording_ref}</small>
      </div>
      <div><span className="row-label">Duration</span><strong>{formatDuration(recording.duration_seconds)}</strong></div>
      <div><span className="row-label">Devices</span><strong>{recording.device_summary.join(" · ") || "Pending"}</strong></div>
      <div><span className="row-label">Status</span><span className={`product-status status-${status.tone}`}>{status.label}</span></div>
      <div className="recording-action">
        {status.viewer ? <Link to={operationalRecordingPath(workspaceRef, recording.recording_ref)}>Open data <span aria-hidden="true">→</span></Link>
          : <span aria-disabled="true">{recording.signals === "available" && recording.viewer === "failed" ? "Viewer unavailable" : "Data processing"}</span>}
      </div>
    </article>
  );
}

export function RecordingsPage() {
  const [toolsOpen, setToolsOpen] = useState(false);
  const workspaces = useOperationalWorkspace();
  const demo = useQuery({ queryKey: operationalKeys.demo(), queryFn: ({ signal }) => getDemoRecording(signal), retry: false });
  const workspaceRef = workspaces.workspace?.slug || "";
  const identity = useSyncExternalStore(subscribeAuthDataIdentity, authDataIdentity, authDataIdentity);
  const selectionScope = `${identity}:${workspaceRef}`;
  const [selection, setSelection] = useState<{scope: string; ids: string[]}>({scope: selectionScope, ids: []});
  useEffect(() => { setSelection({scope: selectionScope, ids: []}); }, [selectionScope]);
  const recordings = useQuery({
    queryKey: operationalKeys.list(workspaceRef),
    queryFn: ({ signal }) => listOperationalRecordings(workspaceRef, signal),
    enabled: Boolean(workspaceRef),
    refetchInterval: (query) => query.state.data?.items.some((item) =>
      (item.signals !== "available" && item.signals !== "failed") || item.hypnogram === "processing" || item.hypnogram === "queued" ||
      (item.signals === "available" && item.viewer !== "available" && item.viewer !== "failed")) ? 5_000 : false,
  });
  const webUploads = shouldExposeWebUploads(
    import.meta.env.VITE_OPERATIONAL_WEB_UPLOADS,
    workspaces.workspace?.permissions || [],
  );
  useEffect(() => { document.title = "Night recordings · Rekonas"; }, []);

  const items = recordings.data?.items || [];
  const selected = selection.scope === selectionScope ? selection.ids.filter(id => items.some(item => item.recording_ref === id)) : [];
  const canExport = workspaces.workspace?.permissions.includes("recordings.export") === true;
  const ready = items.filter((item) => productStatus(item).viewer).length;
  const processing = items.filter((item) => item.state === "processing" || item.state === "signals_available").length;

  if (workspaces.isPending || (workspaces.authorizedCount === 0 && demo.isPending)) return <main className="operational-loading"><Spinner /> Loading authorized recordings…</main>;
  if (workspaces.isError || (workspaces.authorizedCount === 0 && !demo.data)) return (
    <main className="operational-main"><Alert>
      No authorized operational workspace is available for this account.
    </Alert></main>
  );
  return (
    <main className="operational-main">
      <section className="operational-hero">
        <div><p>Operational dashboard</p><h1>Night recordings</h1><span>Review available signals and sleep analysis for authorized recordings.</span>
          {workspaces.authorizedCount > 1 && <label className="workspace-selector">Workspace
            <select value={workspaceRef} onChange={(event) => workspaces.selectWorkspace(event.target.value)}>
              <option value="">Choose a workspace</option>
              {workspaces.workspaces.map((item) => <option key={item.id} value={item.slug}>{item.display_name}</option>)}
            </select>
          </label>}
        </div>
        <dl><div><dt>Recordings</dt><dd>{items.length}</dd></div><div><dt>Viewable</dt><dd>{ready}</dd></div><div><dt>Processing</dt><dd>{processing}</dd></div></dl>
      </section>
      {demo.data && <section className="recording-list-shell demo-recording-entry">
        <header><div><p>Prepared demonstration</p><h2>{demo.data.label}</h2></div>
          <Link to="/recordings-demo">Open demonstration <span aria-hidden="true">→</span></Link></header>
        <p>{demo.data.provenance}</p><small>Duration: {formatDuration(demo.data.duration_seconds)}</small>
      </section>}
      {!workspaces.workspace && <Alert>Choose a real workspace to view recordings or upload data.</Alert>}
      <RecentSummaryEmails />
      {workspaces.workspace && <section className="recording-list-shell">
        <header><div><p>Authorized scope</p><h2>{workspaces.workspace.display_name}</h2></div>
          <div className="recording-list-actions">
            <WebUploadGate flag={import.meta.env.VITE_OPERATIONAL_WEB_UPLOADS} permissions={workspaces.workspace.permissions}>
              <Button size="sm" variant="secondary" aria-expanded={toolsOpen} onClick={() => setToolsOpen((open) => !open)}>Test tools</Button>
            </WebUploadGate>
            <Button className="refresh-button" size="sm" variant="ghost" onClick={() => void recordings.refetch()} disabled={recordings.isFetching}><span className="refresh-icon-slot">{recordings.isFetching && <Spinner />}</span><span>Refresh</span></Button>
          </div>
        </header>
        {webUploads && toolsOpen && <UploadPanel workspaceRef={workspaceRef} />}
        {recordings.isPending && !recordings.data && <div className="operational-loading"><Spinner /> Loading recordings…</div>}
        <div className="recording-refresh-status" aria-live="polite">
          {recordings.isError && recordings.data && <Alert className="recording-list-alert">Refresh failed. The last authorized recording list remains visible.</Alert>}
          {recordings.isError && !recordings.data && <Alert className="recording-list-alert">Recordings could not be loaded. Access is concealed when authorization is absent.</Alert>}
        </div>
        {recordings.isSuccess && items.length === 0 && <div className="recording-empty"><h3>No recordings yet</h3><p>Accepted nightly recordings will appear here while processing.</p></div>}
        {canExport && <div className="summary-selection-bar" key={selectionScope}>
          <strong>{selected.length} recordings selected</strong><span aria-hidden="true"> · </span><SummaryEmailAction ids={selected} />
          <p>Select up to 10 recordings shown on this page. Other pages are never selected automatically.</p>
        </div>}
        {items.map((recording) => <RecordingRow key={recording.recording_ref} recording={recording} workspaceRef={workspaceRef}
          selection={canExport ? {checked: selected.includes(recording.recording_ref), disabled: selected.length >= 10 && !selected.includes(recording.recording_ref),
            toggle: () => setSelection({scope: selectionScope, ids: selected.includes(recording.recording_ref)
              ? selected.filter(id => id !== recording.recording_ref) : [...selected, recording.recording_ref]})} : undefined} />)}
      </section>}
    </main>
  );
}
